// SVG Cover Pattern Generator
// Generates decorative covers based on theme colors and pattern type

const PATTERNS = [
  // Geometric
  { id: 'geo-waves', name: 'Волны', category: 'geometric', render: wavePattern },
  { id: 'geo-circles', name: 'Круги', category: 'geometric', render: circlePattern },
  { id: 'geo-diamonds', name: 'Ромбы', category: 'geometric', render: diamondPattern },
  { id: 'geo-lines', name: 'Линии', category: 'geometric', render: linePattern },
  { id: 'geo-hex', name: 'Соты', category: 'geometric', render: hexPattern },
  // Botanical
  { id: 'bot-leaves', name: 'Листья', category: 'botanical', render: leafPattern },
  { id: 'bot-branches', name: 'Ветки', category: 'botanical', render: branchPattern },
  { id: 'bot-petals', name: 'Лепестки', category: 'botanical', render: petalPattern },
  // Ethnic
  { id: 'eth-runes', name: 'Руны', category: 'ethnic', render: runePattern },
  { id: 'eth-mandala', name: 'Мандала', category: 'ethnic', render: mandalaPattern },
  { id: 'eth-celtic', name: 'Кельтский', category: 'ethnic', render: celticPattern },
  // Abstract
  { id: 'abs-gradient', name: 'Градиент', category: 'abstract', render: gradientPattern },
  { id: 'abs-blobs', name: 'Пятна', category: 'abstract', render: blobPattern },
  { id: 'abs-noise', name: 'Текстура', category: 'abstract', render: noisePattern },
];

// Seeded random for deterministic patterns
function seededRandom(seed) {
  let s = seed;
  return () => { s = (s * 16807 + 0) % 2147483647; return (s - 1) / 2147483646; };
}

function wavePattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const paths = [];
  for (let i = 0; i < 12; i++) {
    const y = 10 + i * (h / 12);
    const amp = 10 + r() * 25;
    const freq = 0.008 + r() * 0.015;
    const phase = r() * Math.PI * 2;
    let d = `M 0 ${y}`;
    for (let x = 0; x <= w; x += 5) d += ` L ${x} ${y + Math.sin(x * freq + phase) * amp}`;
    const sw = 0.8 + r() * 2;
    paths.push(`<path d="${d}" fill="none" stroke="${accent}" stroke-width="${sw}" opacity="${0.08 + r() * 0.18}"/>`);
    if (r() > 0.5) paths.push(`<path d="${d}" fill="none" stroke="${accent}" stroke-width="${sw + 3}" opacity="${0.02 + r() * 0.03}"/>`);
  }
  return paths.join('');
}

function circlePattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const circles = [];
  for (let i = 0; i < 40; i++) {
    const cx = r() * w, cy = r() * h, rad = 8 + r() * 60;
    circles.push(`<circle cx="${cx}" cy="${cy}" r="${rad}" fill="none" stroke="${accent}" stroke-width="${0.5 + r() * 1.5}" opacity="${0.06 + r() * 0.14}"/>`);
    if (r() > 0.6) circles.push(`<circle cx="${cx}" cy="${cy}" r="${rad * 0.6}" fill="${accent}" opacity="${0.02 + r() * 0.04}"/>`);
  }
  return circles.join('');
}

function diamondPattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const items = [];
  const size = 30;
  for (let x = -size; x < w + size; x += size * 1.5) {
    for (let y = -size; y < h + size; y += size * 1.5) {
      const ox = (Math.floor(y / (size * 1.5)) % 2) * size * 0.75;
      items.push(`<rect x="${x + ox}" y="${y}" width="${size * 0.7}" height="${size * 0.7}" transform="rotate(45 ${x + ox + size * 0.35} ${y + size * 0.35})" fill="none" stroke="${accent}" stroke-width="0.8" opacity="${0.2 + r() * 0.2}"/>`);
    }
  }
  return items.join('');
}

function linePattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const lines = [];
  for (let i = 0; i < 15; i++) {
    const x1 = r() * w, y1 = r() * h, angle = r() * Math.PI;
    const len = 50 + r() * 150;
    lines.push(`<line x1="${x1}" y1="${y1}" x2="${x1 + Math.cos(angle) * len}" y2="${y1 + Math.sin(angle) * len}" stroke="${accent}" stroke-width="${0.5 + r()}" opacity="${0.25 + r() * 0.2}"/>`);
  }
  return lines.join('');
}

function hexPattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const items = [];
  const s = 25;
  for (let row = 0; row < h / (s * 1.5) + 1; row++) {
    for (let col = 0; col < w / (s * 1.7) + 1; col++) {
      const cx = col * s * 1.73 + (row % 2) * s * 0.87;
      const cy = row * s * 1.5;
      const pts = Array.from({ length: 6 }, (_, i) => {
        const a = Math.PI / 3 * i - Math.PI / 6;
        return `${cx + s * 0.8 * Math.cos(a)},${cy + s * 0.8 * Math.sin(a)}`;
      }).join(' ');
      items.push(`<polygon points="${pts}" fill="none" stroke="${accent}" stroke-width="0.6" opacity="${0.18 + r() * 0.15}"/>`);
    }
  }
  return items.join('');
}

function leafPattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const items = [];
  for (let i = 0; i < 30; i++) {
    const cx = r() * w, cy = r() * h, rot = r() * 360, sc = 0.4 + r() * 1;
    const op = 0.08 + r() * 0.18;
    items.push(`<g transform="translate(${cx},${cy}) rotate(${rot}) scale(${sc})" opacity="${op}">
      <path d="M0,-22 C12,-16 16,-5 0,22 C-16,-5 -12,-16 0,-22Z" fill="${accent}" opacity="0.15"/>
      <path d="M0,-22 C12,-16 16,-5 0,22 C-16,-5 -12,-16 0,-22Z" fill="none" stroke="${accent}" stroke-width="1.2"/>
      <line x1="0" y1="-20" x2="0" y2="20" stroke="${accent}" stroke-width="0.5" opacity="0.6"/>
      <line x1="-6" y1="-8" x2="0" y2="-3" stroke="${accent}" stroke-width="0.3" opacity="0.5"/>
      <line x1="6" y1="-8" x2="0" y2="-3" stroke="${accent}" stroke-width="0.3" opacity="0.5"/>
    </g>`);
  }
  return items.join('');
}

function branchPattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const items = [];
  for (let i = 0; i < 10; i++) {
    const sx = r() * w, sy = h * r();
    let x = sx, y = sy;
    let d = `M ${x} ${y}`;
    const segments = 5 + Math.floor(r() * 8);
    for (let j = 0; j < segments; j++) {
      x += (r() - 0.5) * 40; y -= 10 + r() * 20; d += ` Q ${x + (r()-0.5)*20} ${y + 10} ${x} ${y}`;
      // Small side branches
      if (r() > 0.4) {
        const bx = x + (r() > 0.5 ? 1 : -1) * (10 + r() * 15);
        const by = y - 5 - r() * 10;
        items.push(`<line x1="${x}" y1="${y}" x2="${bx}" y2="${by}" stroke="${accent}" stroke-width="0.8" opacity="${0.1 + r() * 0.12}" stroke-linecap="round"/>`);
      }
    }
    items.push(`<path d="${d}" fill="none" stroke="${accent}" stroke-width="${1 + r() * 1.5}" opacity="${0.1 + r() * 0.15}" stroke-linecap="round"/>`);
  }
  return items.join('');
}

function petalPattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const items = [];
  for (let i = 0; i < 15; i++) {
    const cx = r() * w, cy = r() * h, rot = r() * 360, sc = 0.4 + r() * 0.6;
    items.push(`<ellipse cx="${cx}" cy="${cy}" rx="${12 * sc}" ry="${25 * sc}" transform="rotate(${rot} ${cx} ${cy})" fill="none" stroke="${accent}" stroke-width="0.8" opacity="${0.22 + r() * 0.18}"/>`);
  }
  return items.join('');
}

function runePattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  // Elder Futhark inspired + more complex shapes
  const runes = [
    'M0,-18 L0,18', 'M-8,-18 L0,0 L8,-18 M0,0 L0,18', 'M-8,-18 L-8,18 M-8,0 L8,-18 L8,18',
    'M0,-18 L10,0 L0,18 L-10,0Z', 'M-8,-18 L8,18 M8,-18 L-8,18', 'M0,-18 L0,18 M-10,0 L10,0',
    'M-8,-18 L0,0 L-8,18 M0,0 L10,0', 'M0,-18 L0,18 M-10,-9 L10,-9 M-10,9 L10,9',
    'M-6,-18 L6,-18 L6,18 L-6,18Z', 'M0,-18 L10,0 L0,18 M0,-18 L-10,0 L0,18',
    'M-8,-18 L8,0 L-8,18 M8,-18 L-8,0 L8,18', 'M0,-18 L0,18 M-10,-18 L10,0 L-10,18',
  ];
  const items = [];
  // Background glow layer
  for (let i = 0; i < 40; i++) {
    const cx = r() * w, cy = r() * h;
    items.push(`<circle cx="${cx}" cy="${cy}" r="${15 + r() * 25}" fill="${accent}" opacity="${0.02 + r() * 0.03}"/>`);
  }
  // Main runes - lots of them in a scattered grid
  const gridSize = 50;
  for (let gx = -20; gx < w + 20; gx += gridSize) {
    for (let gy = -20; gy < h + 20; gy += gridSize) {
      if (r() < 0.35) continue; // skip some for organic feel
      const cx = gx + (r() - 0.5) * 30;
      const cy = gy + (r() - 0.5) * 30;
      const rune = runes[Math.floor(r() * runes.length)];
      const scale = 0.6 + r() * 0.8;
      const rot = (r() - 0.5) * 20;
      const op = 0.12 + r() * 0.25;
      // Glow behind
      items.push(`<g transform="translate(${cx},${cy}) rotate(${rot}) scale(${scale})">
        <path d="${rune}" fill="none" stroke="${accent}" stroke-width="4" stroke-linecap="round" opacity="${op * 0.3}" filter="url(#blur)"/>
        <path d="${rune}" fill="none" stroke="${accent}" stroke-width="2" stroke-linecap="round" opacity="${op}"/>
      </g>`);
    }
  }
  // Add a subtle blur filter definition
  const defs = `<defs><filter id="blur"><feGaussianBlur stdDeviation="3"/></filter></defs>`;
  return defs + items.join('');
}

function mandalaPattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const cx = w / 2, cy = h / 2;
  const items = [];
  const defs = `<defs><filter id="glow"><feGaussianBlur stdDeviation="2"/></filter></defs>`;
  // Many concentric rings with decorations
  for (let ring = 1; ring <= 8; ring++) {
    const rad = ring * 20 + r() * 8;
    const op = 0.1 + r() * 0.15;
    items.push(`<circle cx="${cx}" cy="${cy}" r="${rad}" fill="none" stroke="${accent}" stroke-width="${ring < 3 ? 1.5 : 0.8}" opacity="${op}"/>`);
    // Decorative dots/petals on each ring
    const n = 6 + ring * 3;
    for (let i = 0; i < n; i++) {
      const a = (Math.PI * 2 / n) * i + ring * 0.3;
      const px = cx + Math.cos(a) * rad, py = cy + Math.sin(a) * rad;
      if (ring % 2 === 0) {
        items.push(`<circle cx="${px}" cy="${py}" r="${2 + r() * 3}" fill="${accent}" opacity="${0.08 + r() * 0.1}"/>`);
      } else {
        const sz = 4 + r() * 6;
        items.push(`<ellipse cx="${px}" cy="${py}" rx="${sz}" ry="${sz * 0.4}" transform="rotate(${a * 180 / Math.PI} ${px} ${py})" fill="none" stroke="${accent}" stroke-width="0.7" opacity="${0.12 + r() * 0.12}"/>`);
      }
    }
  }
  // Connecting lines from center
  for (let i = 0; i < 12; i++) {
    const a = (Math.PI * 2 / 12) * i;
    items.push(`<line x1="${cx}" y1="${cy}" x2="${cx + Math.cos(a) * 160}" y2="${cy + Math.sin(a) * 160}" stroke="${accent}" stroke-width="0.5" opacity="0.06"/>`);
  }
  // Central glow
  items.push(`<circle cx="${cx}" cy="${cy}" r="8" fill="${accent}" opacity="0.1" filter="url(#glow)"/>`);
  items.push(`<circle cx="${cx}" cy="${cy}" r="3" fill="${accent}" opacity="0.2"/>`);
  return defs + items.join('');
}

function celticPattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const items = [];
  const s = 40;
  for (let x = s; x < w - s; x += s * 2) {
    for (let y = s; y < h - s; y += s * 2) {
      items.push(`<circle cx="${x}" cy="${y}" r="${s * 0.8}" fill="none" stroke="${accent}" stroke-width="1" opacity="${0.18 + r() * 0.15}"/>`);
      items.push(`<circle cx="${x + s}" cy="${y + s}" r="${s * 0.8}" fill="none" stroke="${accent}" stroke-width="1" opacity="${0.18 + r() * 0.15}"/>`);
    }
  }
  return items.join('');
}

function gradientPattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const angle = Math.floor(r() * 360);
  return `<defs>
    <linearGradient id="g${seed}" gradientTransform="rotate(${angle})">
      <stop offset="0%" stop-color="${accent}" stop-opacity="0.2"/>
      <stop offset="40%" stop-color="${bg}" stop-opacity="0"/>
      <stop offset="60%" stop-color="${accent}" stop-opacity="0.08"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0.15"/>
    </linearGradient>
    <radialGradient id="rg${seed}" cx="${30 + r() * 40}%" cy="${30 + r() * 40}%">
      <stop offset="0%" stop-color="${accent}" stop-opacity="0.12"/>
      <stop offset="100%" stop-color="${accent}" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="url(#g${seed})"/>
  <rect width="${w}" height="${h}" fill="url(#rg${seed})"/>`;
}

function blobPattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const items = [`<defs><filter id="bblur${seed}"><feGaussianBlur stdDeviation="15"/></filter></defs>`];
  for (let i = 0; i < 8; i++) {
    const cx = r() * w, cy = r() * h, rx = 50 + r() * 100, ry = 30 + r() * 70;
    items.push(`<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="${accent}" opacity="${0.04 + r() * 0.08}" transform="rotate(${r() * 180} ${cx} ${cy})" filter="url(#bblur${seed})"/>`);
  }
  // Small sharp accents
  for (let i = 0; i < 15; i++) {
    const cx = r() * w, cy = r() * h;
    items.push(`<circle cx="${cx}" cy="${cy}" r="${3 + r() * 8}" fill="${accent}" opacity="${0.06 + r() * 0.08}"/>`);
  }
  return items.join('');
}

function noisePattern(w, h, accent, bg, seed) {
  const r = seededRandom(seed);
  const items = [];
  for (let i = 0; i < 80; i++) {
    const x = r() * w, y = r() * h, s = 1 + r() * 3;
    items.push(`<rect x="${x}" y="${y}" width="${s}" height="${s}" fill="${accent}" opacity="${0.08 + r() * 0.1}" rx="0.5"/>`);
  }
  return items.join('');
}

export function generateCoverSVG(patternId, width, height, accentColor, bgColor, seed = 42) {
  const pattern = PATTERNS.find(p => p.id === patternId);
  if (!pattern) return '';
  const inner = pattern.render(width, height, accentColor, bgColor, seed);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
    <rect width="${width}" height="${height}" fill="${bgColor}"/>
    ${inner}
  </svg>`;
}

export function getCoverDataURL(patternId, width, height, accent, bg, seed = 42) {
  const svg = generateCoverSVG(patternId, width, height, accent, bg, seed);
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

// Generate gallery: each pattern × multiple seeds
export function generateGallery(accent, bg, count = 4) {
  const gallery = [];
  for (const p of PATTERNS) {
    for (let s = 1; s <= count; s++) {
      gallery.push({ patternId: p.id, seed: s * 17 + 7, name: p.name, category: p.category });
    }
  }
  return gallery;
}

export { PATTERNS };
